import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Zap, Users } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";

const HonestKingdomVoice = () => {
  return (
    <div className="hkv-theme min-h-screen bg-background text-foreground">
      <PageMeta
        title="Honest Kingdom Voice — Dr. Sully"
        description="Dr. Sully's prophetic teaching arm: confronting, teaching, equipping, and leading the modern Kingdom voice."
        path="/honest-kingdom-voice"
      />
      <SiteNav />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 bg-royal-black overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-[0.03] pointer-events-none select-none">
          <div className="font-display font-bold uppercase tracking-[0.5em] text-white whitespace-nowrap text-[20vw] leading-none mt-10">
            KINGDOM VOICE
          </div>
        </div>

        <div className="container relative max-w-4xl text-center">
          <Reveal variant="up">
            <div className="inline-flex items-center gap-3 border border-gold/30 bg-royal-black/50 px-3 py-1.5 rounded-sm mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-gold shadow-[0_0_10px_hsl(var(--gold))]" />
              <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/80">
                Confronting · Teaching · Equipping · Leading
              </span>
            </div>
          </Reveal>

          <Reveal variant="up" delay={80}>
            <h1 className="font-display font-bold uppercase text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-6 leading-[0.95] tracking-tight">
              The Honest<br />
              <span className="text-gradient-gold">Kingdom Voice™</span>
            </h1>
          </Reveal>

          <Reveal variant="fade" delay={160}>
            <p className="text-white/80 text-base md:text-lg mt-7 max-w-2xl mx-auto leading-relaxed">
              Confronting false Christianity. Teaching biblical truth. Equipping leaders to heal, think clearly, and lead with discipline, purpose, and authority.
            </p>
          </Reveal>

          <Reveal variant="up" delay={240}>
            <p className="text-gold font-display uppercase tracking-[0.25em] text-xs mt-8">
              "If it doesn't make sense — make change."
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 md:py-32 bg-royal-black border-b border-gold/20">
        <div className="container max-w-4xl">
          <Reveal variant="up">
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-gold mb-4">
                <span className="h-px w-6 bg-gold" />
                Our Mission
                <span className="h-px w-6 bg-gold" />
              </span>
              <h2 className="font-display font-bold uppercase text-white text-4xl md:text-5xl mt-5 leading-[1.05]">
                Apostolic Truth for<br />
                <span className="text-gradient-gold">Modern Leaders</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: "Confronting False Christianity",
                desc: "Exposing doctrinal error, spiritual manipulation, and theological compromise. Grounded in Scripture, unapologetic in truth.",
              },
              {
                icon: Zap,
                title: "Teaching Biblical Truth",
                desc: "Deep dives into Scripture using Hebrew, Greek, and Strong's Concordance. Clarity over complexity. Authority over accommodation.",
              },
              {
                icon: Users,
                title: "Equipping Leaders",
                desc: "Building disciples who lead with discipline, purpose, and kingdom authority. Healing through truth. Leading through conviction.",
              },
            ].map((item, i) => (
              <Reveal key={item.title} variant="up" delay={i * 80}>
                <div className="bg-royal-black border border-gold/30 p-8 h-full hover:border-gold/60 transition-colors">
                  <item.icon className="h-8 w-8 text-gold" />
                  <h3 className="font-display uppercase text-white text-lg md:text-xl mt-4 tracking-wide">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-sm md:text-base mt-3 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE PRINCIPLES */}
      <section className="py-24 md:py-32 bg-royal-black">
        <div className="container max-w-4xl">
          <Reveal variant="up">
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-gold mb-4">
                <span className="h-px w-6 bg-gold" />
                Core Principles
                <span className="h-px w-6 bg-gold" />
              </span>
              <h2 className="font-display font-bold uppercase text-white text-4xl md:text-5xl mt-5 leading-[1.05]">
                How We Lead
              </h2>
            </div>
          </Reveal>

          <div className="space-y-6">
            {[
              {
                title: "Bible-Literal",
                desc: "Scripture is the final authority. Not cultural, not contextual, not compromised. The Word stands as written.",
              },
              {
                title: "Apostolic-Aligned",
                desc: "Rooted in apostolic doctrine and the foundation of the early church. Pentecostal in power, apostolic in authority.",
              },
              {
                title: "Non-Divisive",
                desc: "We teach truth without creating factions. Unity in essentials, liberty in non-essentials, charity in all things.",
              },
              {
                title: "Kingdom-Focused",
                desc: "Every teaching points to the advancement of God's kingdom. Personal growth serves corporate mission.",
              },
            ].map((principle, i) => (
              <Reveal key={principle.title} variant="up" delay={i * 60}>
                <div className="bg-royal-black border border-gold/20 p-6 md:p-8 hover:border-gold/50 transition-colors">
                  <h3 className="font-display uppercase text-gold text-lg md:text-xl tracking-wide">
                    {principle.title}
                  </h3>
                  <p className="text-white/70 text-base mt-3 leading-relaxed">
                    {principle.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-24 md:py-32 bg-royal-black border-t border-gold/20">
        <div className="container max-w-3xl text-center">
          <Reveal variant="up">
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-5xl leading-[1.05]">
              Join the <span className="text-gradient-gold">Honest Kingdom</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl mx-auto leading-relaxed">
              Subscribe to The Honest Kingdom Voice podcast, join our community, and receive unfiltered biblical teaching delivered with authority and clarity.
            </p>
          </Reveal>

          <Reveal variant="up" delay={120}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://open.spotify.com/show/6t5G1oDtkligs6wi4iGgvZ"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 bg-gold text-royal-black font-display uppercase tracking-[0.2em] text-sm px-7 py-5 hover:bg-gold/90 transition-all"
              >
                Listen to the Podcast
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://substack.com/@drsullyspeaks"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 hover:bg-gold/10 hover:border-gold transition-all"
              >
                Follow on Substack
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default HonestKingdomVoice;
